import { axiosInstance } from "api/axios";
import useCustomMutation from "hooks/useCustomMutation";
import useCustomQuery from "hooks/useCustomQuery";
import { useQueryClient } from "react-query";
import { IWpkNode } from "data/wpk_gminy";

export interface IAccountingBoEntry {
  code: string;
  name?: string;
  boWn: number;
  boMa: number;
}

export interface IAccountingOpeningBalance {
  year: number;
  importedAt: string;
  entries: IAccountingBoEntry[];
}

export interface ISaveOpeningBalanceRequest {
  year: number;
  entries: IAccountingBoEntry[];
}

const getOpeningBalanceApi = async (year: number): Promise<IAccountingOpeningBalance> => {
  // BE zwraca PascalCase (PropertyNamingPolicy = null) — mapujemy do camelCase
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const raw: any = (await axiosInstance.get("/accounting/opening-balance", { params: { year } })).data;
  return {
    year: raw.Year ?? year,
    importedAt: raw.ImportedAt ?? "",
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    entries: (raw.Entries ?? []).map((e: any) => ({
      code: e.Code ?? "",
      name: e.Name ?? undefined,
      boWn: e.BoWn ?? 0,
      boMa: e.BoMa ?? 0,
    })),
  };
};

const saveOpeningBalanceApi = async (req: ISaveOpeningBalanceRequest): Promise<void> =>
  axiosInstance.post("/accounting/opening-balance", req);

export const OPENING_BALANCE_QUERY_KEY = "accounting-opening-balance";

export const useOpeningBalance = (year: number) =>
  useCustomQuery<IAccountingOpeningBalance>(
    [OPENING_BALANCE_QUERY_KEY, year],
    () => getOpeningBalanceApi(year),
  );

export const useSaveOpeningBalance = () => {
  const qc = useQueryClient();
  return useCustomMutation<void, ISaveOpeningBalanceRequest>(
    saveOpeningBalanceApi,
    {
      onSuccess: (_, variables) => {
        // Aktualizuj cache bezpośrednio — bez ponownego GET, który mógłby wrócić z pustymi danymi
        qc.setQueryData<IAccountingOpeningBalance>(
          [OPENING_BALANCE_QUERY_KEY, variables.year],
          { year: variables.year, importedAt: new Date().toISOString(), entries: variables.entries },
        );
      },
    },
  );
};

// ── Dziennik księgowań (migracja) ──────────────────────────────────────────────

export interface IJournalImport {
  fileName?: string;
  systemId?: string;
  /** Nagłówki kolumn jako JSON (string[]). */
  headersJson: string;
  /** Wiersze jako JSON (Record<string,unknown>[]). */
  rowsJson: string;
  rowCount: number;
  importedAt: string;
}

export interface ISaveJournalRequest {
  fileName?: string;
  systemId?: string;
  headersJson: string;
  rowsJson: string;
  rowCount: number;
}

const getJournalApi = async (): Promise<IJournalImport | null> => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const raw: any = (await axiosInstance.get("/accounting/journal")).data;
  if (!raw) return null;
  return {
    fileName: raw.FileName ?? undefined,
    systemId: raw.SystemId ?? undefined,
    headersJson: raw.HeadersJson ?? "[]",
    rowsJson: raw.RowsJson ?? "[]",
    rowCount: raw.RowCount ?? 0,
    importedAt: raw.ImportedAt ?? "",
  };
};

const saveJournalApi = async (req: ISaveJournalRequest): Promise<void> => {
  // BE oczekuje PascalCase (PropertyNamingPolicy = null).
  await axiosInstance.post("/accounting/journal", {
    FileName: req.fileName,
    SystemId: req.systemId,
    HeadersJson: req.headersJson,
    RowsJson: req.rowsJson,
    RowCount: req.rowCount,
  });
};

export const JOURNAL_QUERY_KEY = "accounting-journal";

export const useJournal = () =>
  useCustomQuery<IJournalImport | null>([JOURNAL_QUERY_KEY], getJournalApi);

export const useSaveJournal = () => {
  const qc = useQueryClient();
  return useCustomMutation<void, ISaveJournalRequest>(saveJournalApi, {
    onSuccess: () => qc.invalidateQueries(JOURNAL_QUERY_KEY),
  });
};

// ── Obroty na kontach (silnik księgowania → ZOiS) ──────────────────────────────

export interface IAccountTurnover {
  account: string;
  obrWn: number;
  obrMa: number;
}

const getTurnoversApi = async (from?: string, to?: string): Promise<IAccountTurnover[]> => {
  const raw = (await axiosInstance.get("/accounting/turnovers", { params: { from, to } })).data ?? [];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (raw as any[]).map((r) => ({
    account: r.Account ?? "",
    obrWn: r.ObrWn ?? 0,
    obrMa: r.ObrMa ?? 0,
  }));
};

export const TURNOVERS_QUERY_KEY = "accounting-turnovers";

export const useTurnovers = (from?: string, to?: string) =>
  useCustomQuery<IAccountTurnover[]>(
    [TURNOVERS_QUERY_KEY, from ?? "", to ?? ""],
    () => getTurnoversApi(from, to),
    { staleTime: 15_000 },
  );

// ── Rozrachunki (księga pomocnicza per kontrahent) ─────────────────────────────

export interface IContractorSettlement {
  contractorId: string;
  contractorName?: string;
  totalWn: number;
  totalMa: number;
  saldo: number;
}

const getSettlementsApi = async (contractorId?: string): Promise<IContractorSettlement[]> => {
  const raw = (await axiosInstance.get("/accounting/settlements", { params: { contractorId } })).data ?? [];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (raw as any[]).map((r) => ({
    contractorId: r.ContractorId ?? "",
    contractorName: r.ContractorName ?? undefined,
    totalWn: r.TotalWn ?? 0,
    totalMa: r.TotalMa ?? 0,
    saldo: r.Saldo ?? 0,
  }));
};

export const SETTLEMENTS_QUERY_KEY = "accounting-settlements";

export const useSettlements = (contractorId?: string, enabled = true) =>
  useCustomQuery<IContractorSettlement[]>(
    [SETTLEMENTS_QUERY_KEY, contractorId ?? ""],
    () => getSettlementsApi(contractorId),
    { enabled, staleTime: 15_000 },
  );

// ── Rejestr VAT (z zaksięgowanych dekretów) ────────────────────────────────────

export interface IVatRegisterRow {
  postingId: string;
  entryDate: string;
  documentNumber?: string;
  contractorName?: string;
  type: string; // Purchase | Sale
  net: number;
  vat: number;
  gross: number;
}

const getVatRegisterApi = async (
  from?: string,
  to?: string,
  type?: string,
): Promise<IVatRegisterRow[]> => {
  const raw = (await axiosInstance.get("/accounting/vat-register", { params: { from, to, type } })).data ?? [];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (raw as any[]).map((r) => ({
    postingId: r.PostingId ?? "",
    entryDate: r.EntryDate ?? "",
    documentNumber: r.DocumentNumber ?? undefined,
    contractorName: r.ContractorName ?? undefined,
    type: r.Type ?? "Purchase",
    net: r.Net ?? 0,
    vat: r.Vat ?? 0,
    gross: r.Gross ?? 0,
  }));
};

export const VAT_REGISTER_QUERY_KEY = "accounting-vat-register";

export const useVatRegister = (from?: string, to?: string, type?: string) =>
  useCustomQuery<IVatRegisterRow[]>(
    [VAT_REGISTER_QUERY_KEY, from ?? "", to ?? "", type ?? ""],
    () => getVatRegisterApi(from, to, type),
    { staleTime: 15_000 },
  );

// ── Bilanse (import z systemu FK) ──────────────────────────────────────────────

export interface IBalanceSheet {
  id?: string;
  periodType: "Monthly" | "Annual";
  year: number;
  month: number;
  label: string;
  fixedAssets: number;
  currentAssets: number;
  totalAssets: number;
  equity: number;
  liabilities: number;
  totalLiabilities: number;
  netResult: number;
  positionsJson?: string;
  importedAt?: string;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const mapBalance = (r: any): IBalanceSheet => ({
  id: r.Id ?? undefined,
  periodType: (r.PeriodType ?? "Monthly") as "Monthly" | "Annual",
  year: r.Year ?? 0,
  month: r.Month ?? 0,
  label: r.Label ?? "",
  fixedAssets: r.FixedAssets ?? 0,
  currentAssets: r.CurrentAssets ?? 0,
  totalAssets: r.TotalAssets ?? 0,
  equity: r.Equity ?? 0,
  liabilities: r.Liabilities ?? 0,
  totalLiabilities: r.TotalLiabilities ?? 0,
  netResult: r.NetResult ?? 0,
  importedAt: r.ImportedAt ?? undefined,
});

export const BALANCE_SHEETS_QUERY_KEY = "accounting-balance-sheets";

export const useBalanceSheets = () =>
  useCustomQuery<IBalanceSheet[]>([BALANCE_SHEETS_QUERY_KEY], async () => {
    const raw = (await axiosInstance.get("/accounting/balance-sheets")).data ?? [];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (raw as any[]).map(mapBalance);
  });

export const useSaveBalanceSheets = () => {
  const qc = useQueryClient();
  return useCustomMutation<void, IBalanceSheet[]>(
    (entries) =>
      axiosInstance
        .post("/accounting/balance-sheets", {
          Entries: entries.map((e) => ({
            PeriodType: e.periodType,
            Year: e.year,
            Month: e.month,
            Label: e.label,
            FixedAssets: e.fixedAssets,
            CurrentAssets: e.currentAssets,
            TotalAssets: e.totalAssets,
            Equity: e.equity,
            Liabilities: e.liabilities,
            TotalLiabilities: e.totalLiabilities,
            NetResult: e.netResult,
            PositionsJson: e.positionsJson,
          })),
        })
        .then(() => undefined),
    { onSuccess: () => qc.invalidateQueries([BALANCE_SHEETS_QUERY_KEY]) },
  );
};

// ── JPK / deklaracje podatkowe ─────────────────────────────────────────────────

export interface IJpkFile {
  id: string;
  category: string;
  type: string;
  status: string;
  periodFrom: string;
  periodTo: string;
  fileName: string;
  netAmount: number;
  vatAmount: number;
  grossAmount: number;
  rowCount: number;
  createdAt: string;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const mapJpk = (r: any): IJpkFile => ({
  id: r.Id ?? "",
  category: r.Category ?? "JPK",
  type: r.Type ?? "JPK_V7M",
  status: r.Status ?? "Generated",
  periodFrom: r.PeriodFrom ?? "",
  periodTo: r.PeriodTo ?? "",
  fileName: r.FileName ?? "",
  netAmount: r.NetAmount ?? 0,
  vatAmount: r.VatAmount ?? 0,
  grossAmount: r.GrossAmount ?? 0,
  rowCount: r.RowCount ?? 0,
  createdAt: r.CreatedAt ?? "",
});

export const JPK_QUERY_KEY = "accounting-jpk";

export const useJpkFiles = () =>
  useCustomQuery<IJpkFile[]>([JPK_QUERY_KEY], async () => {
    const raw = (await axiosInstance.get("/accounting/jpk")).data ?? [];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (raw as any[]).map(mapJpk);
  });

export const useGenerateJpk = () => {
  const qc = useQueryClient();
  return useCustomMutation<IJpkFile, { type: string; from?: string; to?: string }>(
    async (req) =>
      mapJpk(
        (await axiosInstance.post("/accounting/jpk/generate", { Type: req.type, From: req.from, To: req.to })).data,
      ),
    { onSuccess: () => qc.invalidateQueries([JPK_QUERY_KEY]) },
  );
};

export const useDeleteJpk = () => {
  const qc = useQueryClient();
  return useCustomMutation<void, string>(
    (id) => axiosInstance.delete(`/accounting/jpk/${id}`).then(() => undefined),
    { onSuccess: () => qc.invalidateQueries([JPK_QUERY_KEY]) },
  );
};

export const downloadJpkFile = async (id: string, fileName: string): Promise<void> => {
  const res = await axiosInstance.get(`/accounting/jpk/${id}/download`, { responseType: "blob" });
  const url = URL.createObjectURL(res.data as Blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName || "jpk.xml";
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

// ── Plany kont ────────────────────────────────────────────────────────────────

export interface IChartOfAccountsPlan {
  id: string;
  title: string;
  templateId: string;
  treeJson: string;
  isActivePostingPlan: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ISaveChartOfAccountsPlanRequest {
  title: string;
  templateId: string;
  treeJson: string;
  isActivePostingPlan: boolean;
}

export interface IUpdateChartOfAccountsPlanRequest {
  id: string;
  title: string;
  treeJson: string;
}

type RawPlan = Record<string, unknown>;

const mapRawToPlan = (r: RawPlan): IChartOfAccountsPlan => ({
  id: String(r.Id ?? ""),
  title: String(r.Title ?? ""),
  templateId: String(r.TemplateId ?? ""),
  treeJson: String(r.TreeJson ?? "[]"),
  isActivePostingPlan: Boolean(r.IsActivePostingPlan),
  createdAt: String(r.CreatedAt ?? ""),
  updatedAt: String(r.UpdatedAt ?? ""),
});

const getChartsOfAccountsApi = async (): Promise<IChartOfAccountsPlan[]> => {
  const raw = ((await axiosInstance.get("/accounting/chart-of-accounts")).data ?? []) as RawPlan[];
  return raw.map(mapRawToPlan);
};

const createChartOfAccountsApi = async (req: ISaveChartOfAccountsPlanRequest): Promise<IChartOfAccountsPlan> => {
  const raw = (await axiosInstance.post("/accounting/chart-of-accounts", req)).data as RawPlan;
  return mapRawToPlan(raw);
};

const updateChartOfAccountsApi = async ({ id, title, treeJson }: IUpdateChartOfAccountsPlanRequest): Promise<void> =>
  axiosInstance.put(`/accounting/chart-of-accounts/${id}`, { title, treeJson });

const deleteChartOfAccountsApi = async (id: string): Promise<void> =>
  axiosInstance.delete(`/accounting/chart-of-accounts/${id}`);

const setActiveChartOfAccountsApi = async ({ id, isActive }: { id: string; isActive: boolean }): Promise<void> =>
  axiosInstance.put(`/accounting/chart-of-accounts/${id}/active`, { isActive });

export const CHARTS_QUERY_KEY = "accounting-charts-of-accounts";

export const useChartsOfAccounts = () =>
  useCustomQuery<IChartOfAccountsPlan[]>([CHARTS_QUERY_KEY], getChartsOfAccountsApi);

export const useCreateChartOfAccounts = () => {
  const qc = useQueryClient();
  return useCustomMutation<IChartOfAccountsPlan, ISaveChartOfAccountsPlanRequest>(
    createChartOfAccountsApi,
    { onSuccess: () => qc.invalidateQueries([CHARTS_QUERY_KEY]) },
  );
};

export const useUpdateChartOfAccounts = () =>
  useCustomMutation<void, IUpdateChartOfAccountsPlanRequest>(updateChartOfAccountsApi);

export const useDeleteChartOfAccounts = () => {
  const qc = useQueryClient();
  return useCustomMutation<void, string>(
    deleteChartOfAccountsApi,
    { onSuccess: () => qc.invalidateQueries([CHARTS_QUERY_KEY]) },
  );
};

export const useSetActiveChartOfAccounts = () => {
  const qc = useQueryClient();
  return useCustomMutation<void, { id: string; isActive: boolean }>(
    setActiveChartOfAccountsApi,
    { onSuccess: () => qc.invalidateQueries([CHARTS_QUERY_KEY]) },
  );
};

/** Parsuje treeJson (string) do IWpkNode[]. Bezpiecznie — błąd JSON = puste drzewo. */
export const parseTreeJson = (treeJson: string): IWpkNode[] => {
  try { return JSON.parse(treeJson) as IWpkNode[]; } catch { return []; }
};
